import {
    getPageSeparatorsYPos,
    getWidgetPageOverflowRows,
    isWidgetTruncated,
} from 'helpers/layoutHelpers';
import _ from 'lodash';
import { WidgetT } from '../../helpers';
import {
    NB_RGL_UNIT_ALLOWED_FIRST_PAGE,
    NB_RGL_UNIT_PER_PRINT_PAGE,
} from '../constants';

const MAX_DUPLICATE_ITERATIONS = 10;

// y is the vertical position in RGL units
export const getPageIndex = (y: number): number => {
    if (y < NB_RGL_UNIT_ALLOWED_FIRST_PAGE) {
        return 0;
    }
    return (
        1 +
        Math.floor(
            (y - NB_RGL_UNIT_ALLOWED_FIRST_PAGE) / NB_RGL_UNIT_PER_PRINT_PAGE,
        )
    );
};

/**
 * Get the y offset to apply depending on the page index.
 *
 * For instance, if a widget w appears on the second page, and we can have 40 rgl units
 * on the first page, then w will positioned on the second page with the coordinate w.y - 40.
 * In this case 40 is the offset.
 */
export const getYOffset = (pageIndex: number): number => {
    if (pageIndex === 0) {
        return 0;
    }
    return (
        NB_RGL_UNIT_ALLOWED_FIRST_PAGE +
        (pageIndex - 1) * NB_RGL_UNIT_PER_PRINT_PAGE
    );
};

export type PrintableWidget = Omit<WidgetT, 'key'> & {
    key: string;
    isTruncatedDuplicate: boolean;
    originalHeight?: number;
};

/**
 * Duplicates widgets that are truncated across pages, adjusting their positions
 * and dimensions to fit within the next page. This process continues iteratively
 * until no truncated widgets remain.
 *
 * @returns An array of `PrintableWidget` objects, including the original widgets
 * and any duplicates created to handle truncation. Each duplicate is marked with
 * `isTruncatedDuplicate: true` and has its position and height adjusted to fit
 * within the page boundaries.
 *
 * The function works as follows:
 * 1. Determines the Y-axis positions of page separators using `getPageSeparatorsYPos`.
 * 2. Initializes the `allWidgets` array with the original widgets, marking them
 *    as not truncated duplicates.
 * 3. Iteratively checks for truncated widgets using `isWidgetTruncated`. For each
 *    truncated widget:
 *    - Creates a duplicate with adjusted position and height to fit on the next page.
 *    - Marks the duplicate as a truncated duplicate and calculates its page row overflow.
 * 4. Adds the duplicates to the `allWidgets` array and repeats the process until
 *    no truncated widgets remain.
 */
export const duplicateTruncatedWidget = (
    widgets: WidgetT[],
): PrintableWidget[] => {
    const separatorsYPos = getPageSeparatorsYPos(widgets, true);
    const allWidgets: PrintableWidget[] = widgets.map((w) => ({
        ...w,
        key: w.key.toString(),
        isTruncatedDuplicate: false,
    }));

    let truncatedWidgets = allWidgets.filter((w) => isWidgetTruncated(w));
    // Just in case, we need to limit the number of iterations to avoid infinite loops
    let iterationCount = 0;
    while (
        truncatedWidgets.length > 0 &&
        iterationCount < MAX_DUPLICATE_ITERATIONS
    ) {
        const duplicatedWidgets = truncatedWidgets.map((widget) => {
            const pageIndex = getPageIndex(widget.position.md.y!) + 1;
            const yOffset = getYOffset(pageIndex);
            const duplicate = {
                ...widget,
                key: `${widget.key}-${pageIndex}`,
                position: {
                    ...widget.position,
                    md: {
                        ...widget.position.md,
                        // Adjust the Y position to fit on the next page
                        y: yOffset,
                        // Adjust the height to remove the widget part on the previous page
                        h:
                            widget.position.md.h -
                            (yOffset - widget.position.md.y),
                    },
                },
                isTruncatedDuplicate: true,
                originalHeight: widget.originalHeight ?? widget.position.md.h,
            };

            return {
                ...duplicate,
                pageRowOverflow: getWidgetPageOverflowRows(
                    duplicate,
                    separatorsYPos,
                    true,
                ),
            };
        });

        allWidgets.push(...duplicatedWidgets);
        truncatedWidgets = duplicatedWidgets.filter((w) =>
            isWidgetTruncated(w),
        );
        iterationCount++;
    }
    return allWidgets;
};

/**
 * Gets the list of widget grouped by page index
 * + duplicates the truncated widgets to fully display them
 */
export const getWidgetsAndDuplicatesByPage = (
    widgets: WidgetT[],
): Record<number, PrintableWidget[]> => {
    const widgetsWithDuplicates = duplicateTruncatedWidget(widgets);
    // split widgets according to the page to which they belong.
    return _.groupBy(widgetsWithDuplicates, (widget) =>
        // TODO StrictNullCheck : Try to remove "!"
        getPageIndex(widget.position.md.y!),
    );
};
